import { VerityInstituteSettings } from "../../types/common";
import { api as defaultApi, VerityApiContext } from "../../api/fetch";
import styles from "./proctoringOptionsGrid.module.css";
import { OptionRelatedInstituteSettings, ProctoringOption } from "@verity/proctoring-option/types";
import {
	getEditQuizStepName,
	PROCTORING_OPTION_TEXT,
	ProctoringOptionCategory,
} from "./option-text/ProctoringOptionText";
import RecordOptions from "./option-sections/RecordOptions";
import VerificationOptions from "./option-sections/VerificationOptions";
import OtherActivitiesOptions from "./option-sections/OtherActivitiesOptions";
import ShowToolsOptions from "./option-sections/ShowToolsOptions";
import i18nPackage from "../../i18n/i18n";
import { ReactNode, useMemo, useState } from "react";
import { CoreAccordion } from "../../core";
import { useProctoringHelpers } from "./hooks/useProctoringHelpers";
import type { ProctoringDataType, ReferenceMaterialType } from "../hooks/state/useSettingsState";
import StepDescription from "../edit-quiz-steps/CommonStepComponents";
import { EnabledProctoringOptions } from "@verity/api-client/types";
import { ProctoringOptionContext } from "../context/EditQuizContext";
import { EditQuizPageProps } from "../editQuiz";
import { Typography } from "@mui/material";
import { useMobileLayout } from "../../hooks";

export type ProctoringOptionsProps = {
	data: Omit<ProctoringDataType, "thirdPartyPlatforms">;
	currentCategory?: ProctoringOptionCategory;
	hasPassword?: boolean;
	isThirdPartyEnabled?: boolean;
	verityInstituteSettings: VerityInstituteSettings;
	enabledProctoringOptions: EnabledProctoringOptions;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	proctoringOption: Partial<ProctoringOption>;
	referenceMaterialFiles?: ReferenceMaterialType[];
	onUpdateReferenceMaterialFiles?: (files: ReferenceMaterialType[]) => void;
	referenceMaterialToggleOnly?: boolean;
} & Pick<EditQuizPageProps, "setProctoringOption" | "enforcedProfilesOnly" | "quizType" | "api">;

export type CategoryOptionsListProps = {
	hasPassword?: boolean;
	isThirdPartyEnabled?: boolean;
	data: ProctoringOptionsProps["data"];
};

const RenderOptions = ({
	useAccordion,
	expanded,
	categoryTitle,
	handleExpandedChange,
	children,
}: {
	optionsKey: ProctoringOptionCategory;
	useAccordion?: boolean;
	expanded: boolean;
	categoryTitle: ReactNode;
	handleExpandedChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
	children: ReactNode;
}) =>
	useAccordion ? (
		<CoreAccordion
			summary={<Typography className={styles.categoryTitle}>{categoryTitle}</Typography>}
			expanded={expanded}
			handleExpandedChange={handleExpandedChange}
			sx={{ borderRadius: "7px", boxShadow: "0 0 5px 0 rgb(0 0 0/20%)" }}
			disableGutters
		>
			<div className={styles.accordianBody}>{children}</div>
		</CoreAccordion>
	) : (
		children
	);

export function ProctoringOptions({
	currentCategory,
	hasPassword,
	isThirdPartyEnabled,
	data,
	enabledProctoringOptions,
	proctoringOption,
	verityInstituteSettings,
	optionRelatedInstituteSettings,
	enforcedProfilesOnly,
	setProctoringOption,
	quizType = "lms",
	referenceMaterialFiles,
	onUpdateReferenceMaterialFiles,
	referenceMaterialToggleOnly,
	api = defaultApi,
}: ProctoringOptionsProps) {
	const { allowedCategories, isOptionAllowed } = useProctoringHelpers({
		enabledProctoringOptions,
		isThirdPartyEnabled,
		optionRelatedInstituteSettings,
		proctoringOption,
		quizType,
		verityInstituteSettings,
	});
	const isMobileLayout = useMobileLayout();

	const [expandedCategory, setExpandedCategory] = useState<ProctoringOptionCategory | undefined>(undefined);
	const handleExpandedChange =
		(category: ProctoringOptionCategory) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
			setExpandedCategory(isExpanded ? category : undefined);
		};
	const getCategoryTitle = (categoryName: ProctoringOptionCategory) => {
		const categoryNumber = allowedCategories.findIndex((category) => category === categoryName) + 1;
		return (
			<span>
				{categoryNumber}. {getEditQuizStepName(categoryName)}
			</span>
		);
	};

	const CATEGORY_COMPONENTS: Record<ProctoringOptionCategory, ReactNode> = useMemo(
		() => ({
			record: <RecordOptions data={data} />,
			verification: <VerificationOptions />,
			otherActivities: (
				<OtherActivitiesOptions data={data} hasPassword={hasPassword} isThirdPartyEnabled={isThirdPartyEnabled} />
			),
			showTools: <ShowToolsOptions />,
		}),
		[data, hasPassword, isThirdPartyEnabled],
	);

	const isListView = !currentCategory;
	const categoriesToRender = currentCategory ? [currentCategory] : allowedCategories;

	const areAllProctoringOptionsDisabled = Object.entries(PROCTORING_OPTION_TEXT)
		.filter(([, { settingDescription }]) => settingDescription)
		.every(([o]) => !isOptionAllowed(o as keyof ProctoringOption));

	return (
		<VerityApiContext.Provider value={api}>
			<ProctoringOptionContext.Provider
				value={{
					enabledProctoringOptions,
					enforcedProfilesOnly,
					isThirdPartyEnabled: Boolean(isThirdPartyEnabled),
					optionRelatedInstituteSettings,
					proctoringOption,
					setProctoringOption,
					verityInstituteSettings,
					isMobileLayout,
					quizType,
					referenceMaterialFiles,
					onUpdateReferenceMaterialFiles,
					referenceMaterialToggleOnly,
				}}
			>
				<div className={styles.listWrapper}>
					<StepDescription>
						{areAllProctoringOptionsDisabled
							? i18nPackage.t("editQuizPage.configurationStep.allDisabled")
							: i18nPackage.t("editQuizPage.configurationStep.description")}
					</StepDescription>

					{categoriesToRender.map((catKey) => (
						<RenderOptions
							key={catKey}
							optionsKey={catKey}
							useAccordion={isListView}
							expanded={expandedCategory === catKey}
							categoryTitle={getCategoryTitle(catKey)}
							handleExpandedChange={handleExpandedChange(catKey)}
						>
							{CATEGORY_COMPONENTS[catKey]}
						</RenderOptions>
					))}
				</div>
			</ProctoringOptionContext.Provider>
		</VerityApiContext.Provider>
	);
}

export default ProctoringOptions;
